
import React, { useRef, useState } from 'react';
import { X, Camera, Upload, Loader2, AlertTriangle, CheckCircle2, ScanLine, Trash2, UserPlus } from 'lucide-react';
import { UnitSystem, formatHeight, formatWeight } from '../lib/measurements';
import { extractRosterFromImage } from '../services/geminiService';

const POSITIONS = ['Setter', 'Outside Hitter', 'Opposite', 'Middle Blocker', 'Libero', 'DS', 'Unknown'];

export interface ScannedPlayer {
  id: string;
  name: string;
  jerseyNumber: string;
  position: string;
  heightCm: number | null;
  weightKg: number | null;
  handedness: string;
  gradYear: string;
}

interface RosterScannerProps {
  onApply: (players: ScannedPlayer[]) => Promise<void>;
  onClose: () => void;
  unitSystem?: UnitSystem;
  remainingSlots: number;
}

type Step = 'instructions' | 'processing' | 'review';

function toDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const RosterScanner: React.FC<RosterScannerProps> = ({ onApply, onClose, unitSystem = 'imperial' as UnitSystem, remainingSlots }) => {
  const [step, setStep] = useState<Step>('instructions');
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [players, setPlayers] = useState<ScannedPlayer[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const processImage = async (file: File) => {
    setError(null);
    setStep('processing');
    try {
      const uri = await toDataUri(file);
      setImageUri(uri);
      const extracted = await extractRosterFromImage(uri);

      if (extracted.length === 0) {
        setError('No players were found in the image. Make sure the roster is clearly visible and try again.');
        setStep('instructions');
        return;
      }

      setPlayers(extracted.map((p, i) => ({
        id: `scan-${Date.now()}-${i}`,
        name: p.name || '',
        jerseyNumber: p.jerseyNumber || '',
        position: POSITIONS.includes(p.position) ? p.position : 'Unknown',
        heightCm: p.heightCm ?? null,
        weightKg: p.weightKg ?? null,
        handedness: p.handedness || '',
        gradYear: p.gradYear || '',
      })));
      setStep('review');
    } catch (e: any) {
      setError(e?.message || 'Failed to read roster. Please try a clearer photo.');
      setStep('instructions');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processImage(file);
    e.target.value = '';
  };

  const updatePlayer = (id: string, field: keyof ScannedPlayer, value: string) => {
    setPlayers(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const removePlayer = (id: string) => {
    setPlayers(prev => prev.filter(p => p.id !== id));
  };

  const handleApply = async () => {
    const valid = players.filter(p => p.name.trim()).slice(0, remainingSlots);
    if (valid.length === 0) return;
    setIsImporting(true);
    try {
      await onApply(valid);
    } catch (e: any) {
      setError(e?.message || 'Failed to import players. Please try again.');
      setIsImporting(false);
    }
  };

  const validCount = Math.min(players.filter(p => p.name.trim()).length, remainingSlots);
  const overCap = players.filter(p => p.name.trim()).length > remainingSlots;

  return (
    <div className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-screen sm:max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 shrink-0">
          <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
            <ScanLine className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-slate-900 text-sm">Scan Roster</h2>
            <p className="text-xs text-slate-500">Screenshot → auto-import players</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">

          {/* ── Instructions ── */}
          {step === 'instructions' && (
            <div className="p-5 space-y-5">
              {error && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl p-3">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Fields the AI will extract</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Name', icon: '👤' },
                    { label: 'Jersey #', icon: '🔢' },
                    { label: 'Position', icon: '📍' },
                    { label: 'Height', icon: '📏' },
                    { label: 'Handedness', icon: '✋' },
                    { label: 'Grad Year', icon: '🎓' },
                  ].map(({ label, icon }) => (
                    <div key={label} className="bg-slate-50 rounded-lg p-2 text-center">
                      <div className="text-lg">{icon}</div>
                      <div className="text-[11px] font-semibold text-slate-600 mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">How it works</p>
                {[
                  'Find a roster on MaxPreps, a club website, or any other source',
                  'Take a screenshot and upload it here',
                  'AI will extract every player automatically — review before importing',
                ].map((tip, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <p className="text-sm text-slate-600">{tip}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-bold py-3 rounded-xl transition-all"
                >
                  <Upload className="w-4 h-4" /> Upload Screenshot
                </button>
                <button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className="flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 active:scale-95 text-slate-700 font-bold py-3 rounded-xl transition-all"
                >
                  <Camera className="w-4 h-4" /> Take Photo
                </button>
              </div>

              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            </div>
          )}

          {/* ── Processing ── */}
          {step === 'processing' && (
            <div className="p-8 flex flex-col items-center justify-center gap-5 min-h-[300px]">
              {imageUri && (
                <img src={imageUri} alt="Roster" className="w-32 h-32 object-cover rounded-xl border border-slate-200 shadow" />
              )}
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                <p className="font-semibold text-slate-700">Reading roster…</p>
                <p className="text-xs text-slate-400">AI is extracting player information</p>
              </div>
            </div>
          )}

          {/* ── Review ── */}
          {step === 'review' && (
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-3">
                {imageUri && (
                  <img src={imageUri} alt="Roster" className="w-14 h-14 object-cover rounded-lg border border-slate-200 shadow shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">
                    {players.length} player{players.length !== 1 ? 's' : ''} found
                  </p>
                  <p className="text-xs text-slate-500">Edit any fields before importing</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setStep('instructions'); setError(null); setPlayers([]); }}
                  className="text-xs text-emerald-600 hover:text-emerald-800 font-medium shrink-0"
                >
                  Retake
                </button>
              </div>

              {overCap && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Your team has room for {remainingSlots} more player{remainingSlots !== 1 ? 's' : ''}. Only the first {remainingSlots} will be imported.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                {players.map((p, idx) => {
                  const isOverCap = idx >= remainingSlots;
                  return (
                    <div
                      key={p.id}
                      className={`border rounded-xl p-3 space-y-2 transition-opacity ${isOverCap ? 'opacity-40 border-slate-100 bg-slate-50' : 'border-slate-200 bg-white'}`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={p.jerseyNumber}
                          onChange={e => updatePlayer(p.id, 'jerseyNumber', e.target.value)}
                          placeholder="#"
                          disabled={isOverCap}
                          className="w-12 text-center text-sm font-bold border border-slate-200 rounded-lg px-1 py-1.5 outline-none focus:ring-2 focus:ring-emerald-400 disabled:bg-slate-50 disabled:text-slate-400"
                        />
                        <input
                          type="text"
                          value={p.name}
                          onChange={e => updatePlayer(p.id, 'name', e.target.value)}
                          placeholder="Player name"
                          disabled={isOverCap}
                          className="flex-1 text-sm font-semibold border border-slate-200 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-emerald-400 disabled:bg-slate-50 disabled:text-slate-400"
                        />
                        <button
                          type="button"
                          onClick={() => removePlayer(p.id)}
                          className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          value={p.position}
                          onChange={e => updatePlayer(p.id, 'position', e.target.value)}
                          disabled={isOverCap}
                          className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-emerald-400 bg-white text-slate-700 disabled:bg-slate-50 disabled:text-slate-400"
                        >
                          {POSITIONS.map(pos => (
                            <option key={pos} value={pos}>{pos}</option>
                          ))}
                        </select>

                        {p.heightCm != null && (
                          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded-lg">
                            📏 {formatHeight(p.heightCm, unitSystem)}
                          </span>
                        )}
                        {p.weightKg != null && (
                          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded-lg">
                            ⚖️ {formatWeight(p.weightKg, unitSystem)}
                          </span>
                        )}
                        {p.handedness && (
                          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded-lg">
                            ✋ {p.handedness}
                          </span>
                        )}
                        {p.gradYear && (
                          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded-lg">
                            🎓 {p.gradYear}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}

                {players.length === 0 && (
                  <p className="text-center py-6 text-slate-400 text-sm italic">All players removed. Tap Retake to try again.</p>
                )}
              </div>

              {error && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl p-3">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'review' && (
          <div className="flex gap-2 p-4 border-t border-slate-100 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-slate-200 text-slate-600 font-bold rounded-xl text-sm hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={validCount === 0 || isImporting}
              className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isImporting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</>
              ) : (
                <><UserPlus className="w-4 h-4" /> Import {validCount} Player{validCount !== 1 ? 's' : ''}</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RosterScanner;
